// =============================================================================
// VariantOverridesPanel — Phase 3c per-variant config matrix
// -----------------------------------------------------------------------------
// Renders the (BlankVariant × PrintArea) override editor described in the
// apparel multi-side plan ("Variant matrix") — `mockupMode`-aware so the
// same component drives both apparel rendering strategies:
//
//   MASK  (single PNG mockup, per-variant background colour)
//     Columns:  Variant │ Background color │ Required │ Print area │ Actions
//     Tools  :  Auto-detect colours from option-value names; "Apply to
//               <Color>" replicates a hex to siblings sharing the colour.
//
//   PER_VARIANT  (one fully-rendered mockup per variant)
//     Columns:  Variant │ Mockup image │ Required │ Print area │ Actions
//     Tools  :  Background-image upload triggers the ApplyToScopeDialog
//               so a single upload can cover every "Black" variant in
//               one PUT.
//
// The "Print area" column is shared by both modes: it opens the
// per-variant print-area editor (rect + W/H overrides). The button is
// always visible but stays disabled until the editor has a usable
// canvas background — the side mask PNG in MASK mode, the variant /
// side mockup in PER_VARIANT mode. Saving re-opens ApplyToScopeDialog
// so the rect can be bulk-applied across any variant option axis.
//
// The "Required" column is identical in both modes — it overrides the
// SIDE-LEVEL `customizationMandatory` flag for THIS variant only:
//   - Inherit  → use the side default (most rows)
//   - Required → designer blocks "Create Product" if no design on this side
//   - Optional → designer allows save without a design on this side
//
// All persistence flows through `apparel-sides.ts`. Mutations are
// optimistic; on error we roll back the cached row to its prior value
// and surface a toast. Bulk operations use the atomic
// PUT /print-areas/:id/variant-configs endpoint so the entire payload
// either applies or does not.
// =============================================================================

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Card, Button, Spinner, Badge, Input } from '@tkpod/ui';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import {
  autoDetectApparelColors,
  bulkUpsertVariantConfigs,
  deleteVariantConfig,
  listVariantConfigsForPrintArea,
  upsertVariantConfig,
  uploadVariantConfigBackground,
  type AutoDetectColorSuggestion,
  type PrintAreaMockupMode,
  type PrintAreaSummary,
  type PrintAreaVariantConfig,
  type UpsertVariantConfigPayload,
  type VariantConfigRow,
} from '@/lib/api/apparel-sides';
import {
  ApplyToScopeDialog,
  rowMatchesOptionScope,
  type ApplyScope,
} from './ApplyToScopeDialog';

// Lazy-load the variant-scoped print-area editor — its DOM canvas + drag
// math only matters for PER_VARIANT sides, and even there only after a
// merchant clicks "Edit print area" on a row that already has a mockup.
const VariantPrintAreaEditorModal = lazy(() =>
  import('./VariantPrintAreaEditorModal').then((m) => ({
    default: m.VariantPrintAreaEditorModal,
  })),
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const normalizeHex = (raw: string): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (HEX_RE.test(trimmed)) return trimmed.toUpperCase();
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`.toUpperCase();
  return null;
};

const QUERY_KEY = (templateId: string, printAreaId: string) =>
  ['admin-variant-configs', templateId, printAreaId] as const;

// ---------------------------------------------------------------------------
// Bulk-payload helpers
// ---------------------------------------------------------------------------
//
// `bulkUpsertVariantConfigs` is canonical-state: any field omitted from an
// item's body is coerced to `null` server-side, AND any blankVariantId not
// present in the items array is hard-deleted in the same transaction (see
// `apps/api/src/modules/catalog/sides.service.ts#bulkUpsertVariantConfigs`).
//
// That means EVERY bulk-apply path must serialise the COMPLETE current
// state of every row it cares about — including override columns the
// caller doesn't intend to change. Forgetting a column silently wipes
// that override across the entire print area, and forgetting a whole
// row silently deletes its config (incl. any standalone W/H overrides).
//
// `buildCanonicalItem` centralises the "merge per-row state with caller
// overrides" step so every bulk-apply flow uses the same wire shape.
// `hasAnyConfigField` is the corresponding empty-row predicate — it
// MUST stay in lockstep with `buildCanonicalItem` (every persisted
// column is checked).

const buildCanonicalItem = (
  row: VariantConfigRow,
  overrides: Partial<Omit<PrintAreaVariantConfig, 'blankVariantId'>>,
): PrintAreaVariantConfig => {
  const cfg = row.currentConfig;
  return {
    blankVariantId: row.blankVariantId,
    backgroundColorHex:
      'backgroundColorHex' in overrides
        ? (overrides.backgroundColorHex ?? null)
        : (cfg?.backgroundColorHex ?? null),
    backgroundImageUrl:
      'backgroundImageUrl' in overrides
        ? (overrides.backgroundImageUrl ?? null)
        : (cfg?.backgroundImageUrl ?? null),
    printAreaOverride:
      'printAreaOverride' in overrides
        ? (overrides.printAreaOverride ?? null)
        : (cfg?.printAreaOverride ?? null),
    customizationMandatory:
      'customizationMandatory' in overrides
        ? (overrides.customizationMandatory ?? null)
        : (cfg?.customizationMandatory ?? null),
    printAreaWidthInOverride:
      'printAreaWidthInOverride' in overrides
        ? (overrides.printAreaWidthInOverride ?? null)
        : (cfg?.printAreaWidthInOverride ?? null),
    printAreaHeightInOverride:
      'printAreaHeightInOverride' in overrides
        ? (overrides.printAreaHeightInOverride ?? null)
        : (cfg?.printAreaHeightInOverride ?? null),
    // Scale-to-product calibration breadcrumb. The bulk PUT coerces any
    // omitted field to null server-side, so leaving this out of the
    // canonical item silently WIPED every variant's calibration on every
    // bulk apply (mockup broadcast, color auto-detect, print-area
    // apply-to-all). Preserve each row's own value verbatim — bulk flows
    // never propagate calibration across rows because it encodes
    // measurements against a specific mockup image.
    scaleCalibrationOverride:
      'scaleCalibrationOverride' in overrides
        ? (overrides.scaleCalibrationOverride ?? null)
        : (cfg?.scaleCalibrationOverride ?? null),
  };
};

const hasAnyConfigField = (item: PrintAreaVariantConfig): boolean =>
  !!item.backgroundColorHex ||
  !!item.backgroundImageUrl ||
  !!item.printAreaOverride ||
  (item.customizationMandatory !== null &&
    item.customizationMandatory !== undefined) ||
  (item.printAreaWidthInOverride !== null &&
    item.printAreaWidthInOverride !== undefined) ||
  (item.printAreaHeightInOverride !== null &&
    item.printAreaHeightInOverride !== undefined) ||
  (item.scaleCalibrationOverride !== null &&
    item.scaleCalibrationOverride !== undefined);

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface VariantOverridesPanelProps {
  templateId: string;
  printAreas: PrintAreaSummary[];
}

export function VariantOverridesPanel({
  templateId,
  printAreas,
}: VariantOverridesPanelProps) {
  const [activePrintAreaId, setActivePrintAreaId] = useState<string | null>(
    () => printAreas[0]?.id ?? null,
  );

  // Reconcile tab selection with the parent list across THREE transitions:
  //
  //   1. The active id was deleted by another mutation (or the parent
  //      flipped the side's `mockupMode` away from PER_VARIANT, removing
  //      it from the panel's filtered slice).
  //   2. We initialised with `null` because `printAreas` was empty at
  //      mount, and the parent now has data (this happens whenever a
  //      template starts MASK-only and the merchant later flips a side
  //      to PER_VARIANT — the previous if-set-state-in-render code
  //      silently kept the panel hidden forever).
  //   3. The list shrank back to empty — clear the selection so the
  //      `printAreas.length === 0` early-return below doesn't keep a
  //      stale tab id in state for the next non-empty render.
  //
  // Living in `useEffect` (instead of the render body) avoids the
  // anti-pattern setState-during-render and stays compatible with
  // React Strict Mode's double-invoke checks.
  useEffect(() => {
    if (printAreas.length === 0) {
      if (activePrintAreaId !== null) setActivePrintAreaId(null);
      return;
    }
    const isStillValid =
      activePrintAreaId !== null &&
      printAreas.some((p) => p.id === activePrintAreaId);
    if (!isStillValid) {
      setActivePrintAreaId(printAreas[0].id);
    }
  }, [printAreas, activePrintAreaId]);

  if (printAreas.length === 0 || !activePrintAreaId) {
    return null;
  }

  const activePrintArea =
    printAreas.find((p) => p.id === activePrintAreaId) ?? printAreas[0];
  const isMaskMode = activePrintArea.mockupMode === 'MASK';

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
        <div>
          <h3 className="text-base font-semibold text-text-primary">
            Variant overrides
          </h3>
          <p className="mt-1 text-xs text-text-secondary">
            {isMaskMode
              ? 'Per-variant background colour and customization requirement. The designer composes each variant\'s colour behind the mockup PNG.'
              : 'Per-variant mockup image, optional print-area override and customization requirement. The designer renders each variant\'s own mockup in place of the side default.'}
          </p>
        </div>
        <PrintAreaTabs
          printAreas={printAreas}
          activeId={activePrintAreaId}
          onSelect={setActivePrintAreaId}
        />
      </div>

      <VariantOverridesTable
        key={activePrintAreaId}
        templateId={templateId}
        printArea={activePrintArea}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PrintArea tabs
// ---------------------------------------------------------------------------

function PrintAreaTabs({
  printAreas,
  activeId,
  onSelect,
}: {
  printAreas: PrintAreaSummary[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Select print area"
      className="flex flex-wrap gap-1 rounded-md bg-surface-tertiary p-1"
    >
      {printAreas.map((pa) => {
        const isActive = pa.id === activeId;
        return (
          <button
            key={pa.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(pa.id)}
            className={[
              'inline-flex items-center gap-2 rounded px-3 py-1.5 text-xs font-medium transition-colors',
              isActive
                ? 'bg-surface text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary',
            ].join(' ')}
          >
            {pa.displayName}
            {pa.variantConfigCount > 0 && (
              <Badge variant="default" size="sm">
                {pa.variantConfigCount}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table — variant rows for the active PrintArea
// ---------------------------------------------------------------------------

function VariantOverridesTable({
  templateId,
  printArea,
}: {
  templateId: string;
  printArea: PrintAreaSummary;
}) {
  const qc = useQueryClient();
  const queryKey = QUERY_KEY(templateId, printArea.id);

  const {
    data: rows = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<VariantConfigRow[]>({
    queryKey,
    queryFn: () => listVariantConfigsForPrintArea(templateId, printArea.id),
  });

  // -------------------------------------------------------------------------
  // Cache helpers — keep optimistic UI consistent with server responses
  // -------------------------------------------------------------------------

  const applyConfigToCache = useCallback(
    (
      blankVariantId: string,
      next: VariantConfigRow['currentConfig'],
    ) => {
      qc.setQueryData<VariantConfigRow[]>(queryKey, (prev) =>
        prev
          ? prev.map((r) =>
              r.blankVariantId === blankVariantId
                ? { ...r, currentConfig: next }
                : r,
            )
          : prev,
      );
    },
    [qc, queryKey],
  );

  const replaceCacheRows = useCallback(
    (mapByVariantId: Map<string, PrintAreaVariantConfig>) => {
      qc.setQueryData<VariantConfigRow[]>(queryKey, (prev) =>
        prev
          ? prev.map((r) => {
              const cfg = mapByVariantId.get(r.blankVariantId);
              if (!cfg && !r.currentConfig) return r;
              if (!cfg) return { ...r, currentConfig: null };
              return {
                ...r,
                currentConfig: {
                  // `id` only becomes non-null after the server has
                  // persisted the row; bulk responses always include it,
                  // but we still defend against a missing field by
                  // preferring the prior cached id and falling back to
                  // null (never an empty string — see VariantConfigRow).
                  id: cfg.id ?? r.currentConfig?.id ?? null,
                  backgroundColorHex: cfg.backgroundColorHex ?? null,
                  backgroundImageUrl: cfg.backgroundImageUrl ?? null,
                  printAreaOverride: cfg.printAreaOverride ?? null,
                  customizationMandatory: cfg.customizationMandatory ?? null,
                  // Per-variant W/H overrides — bulk responses always
                  // include them, but the optimistic path treats
                  // `undefined` as "keep prior" so a partial bulk-apply
                  // (e.g. only the mockup column) doesn't accidentally
                  // wipe a previously configured 10×12 size.
                  printAreaWidthInOverride:
                    cfg.printAreaWidthInOverride === undefined
                      ? (r.currentConfig?.printAreaWidthInOverride ?? null)
                      : cfg.printAreaWidthInOverride,
                  printAreaHeightInOverride:
                    cfg.printAreaHeightInOverride === undefined
                      ? (r.currentConfig?.printAreaHeightInOverride ?? null)
                      : cfg.printAreaHeightInOverride,
                  scaleCalibrationOverride:
                    cfg.scaleCalibrationOverride === undefined
                      ? (r.currentConfig?.scaleCalibrationOverride ?? null)
                      : cfg.scaleCalibrationOverride,
                  updatedAt:
                    r.currentConfig?.updatedAt ?? new Date().toISOString(),
                },
              };
            })
          : prev,
      );
    },
    [qc, queryKey],
  );

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const upsertCellMutation = useMutation({
    mutationFn: ({
      blankVariantId,
      payload,
    }: {
      blankVariantId: string;
      payload: UpsertVariantConfigPayload;
    }) => upsertVariantConfig(templateId, printArea.id, blankVariantId, payload),
    onMutate: async ({ blankVariantId, payload }) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<VariantConfigRow[]>(queryKey);
      const target = prev?.find((r) => r.blankVariantId === blankVariantId);
      const merged: VariantConfigRow['currentConfig'] = {
        // Optimistic merge: we don't yet know the persisted row's cuid
        // (or the current row may have never been saved). Always default
        // to `null` rather than an empty string so the contract — "id is
        // either a real cuid or null while pending" — holds. The server's
        // response in onSuccess overwrites this with the canonical id.
        id: target?.currentConfig?.id ?? null,
        backgroundColorHex:
          payload.backgroundColorHex === undefined
            ? (target?.currentConfig?.backgroundColorHex ?? null)
            : payload.backgroundColorHex || null,
        backgroundImageUrl:
          payload.backgroundImageUrl === undefined
            ? (target?.currentConfig?.backgroundImageUrl ?? null)
            : payload.backgroundImageUrl || null,
        printAreaOverride:
          payload.printAreaOverride === undefined
            ? (target?.currentConfig?.printAreaOverride ?? null)
            : (payload.printAreaOverride ?? null),
        customizationMandatory:
          payload.customizationMandatory === undefined
            ? (target?.currentConfig?.customizationMandatory ?? null)
            : payload.customizationMandatory,
        // Same `undefined` = preserve, `null` = clear, number = override
        // contract as the other override columns. Single-row upsert
        // preserves this distinction so a merchant can clear ONE
        // dimension override without touching siblings.
        printAreaWidthInOverride:
          payload.printAreaWidthInOverride === undefined
            ? (target?.currentConfig?.printAreaWidthInOverride ?? null)
            : (payload.printAreaWidthInOverride ?? null),
        printAreaHeightInOverride:
          payload.printAreaHeightInOverride === undefined
            ? (target?.currentConfig?.printAreaHeightInOverride ?? null)
            : (payload.printAreaHeightInOverride ?? null),
        scaleCalibrationOverride:
          payload.scaleCalibrationOverride === undefined
            ? (target?.currentConfig?.scaleCalibrationOverride ?? null)
            : (payload.scaleCalibrationOverride ?? null),
        updatedAt: new Date().toISOString(),
      };
      applyConfigToCache(blankVariantId, merged);
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast(err.message || 'Failed to save variant override.', 'error');
    },
    onSuccess: (data, vars) => {
      applyConfigToCache(vars.blankVariantId, {
        // Server contract: a successful upsert response always carries a
        // cuid. The `?? null` is purely defensive — it must not drop into
        // an empty string because that would silently produce an invalid
        // cache entry that no consumer can reason about.
        id: data.id ?? null,
        backgroundColorHex: data.backgroundColorHex ?? null,
        backgroundImageUrl: data.backgroundImageUrl ?? null,
        printAreaOverride: data.printAreaOverride ?? null,
        customizationMandatory: data.customizationMandatory ?? null,
        printAreaWidthInOverride: data.printAreaWidthInOverride ?? null,
        printAreaHeightInOverride: data.printAreaHeightInOverride ?? null,
        scaleCalibrationOverride: data.scaleCalibrationOverride ?? null,
        updatedAt: new Date().toISOString(),
      });
    },
  });

  const resetRowMutation = useMutation({
    mutationFn: (blankVariantId: string) =>
      deleteVariantConfig(templateId, printArea.id, blankVariantId),
    onMutate: async (blankVariantId) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<VariantConfigRow[]>(queryKey);
      applyConfigToCache(blankVariantId, null);
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast(err.message || 'Failed to clear variant override.', 'error');
    },
    onSuccess: () => {
      // Server may have updated the parent print area's variantConfigCount;
      // invalidate the parent query so the badge stays accurate.
      qc.invalidateQueries({ queryKey: ['admin-print-areas', templateId] });
    },
  });

  // Apply-scope dialog state — tracks the most recent override change so the
  // merchant can choose whether to propagate it to sibling variants. We only
  // open the dialog when there's more than one row, so single-variant
  // templates fall through to the silent single-row save.
  const [pendingApply, setPendingApply] = useState<
    | {
        sourceRow: VariantConfigRow;
        backgroundImageUrl: string;
      }
    | null
  >(null);

  // Print-area editor scope dialog — opens after a successful single-row
  // save in `VariantPrintAreaEditorModal` so the merchant can broadcast
  // the freshly persisted rect + W/H to sibling variants without
  // re-opening the modal six times for "All Black" or "All variants".
  // Mirrors `pendingApply`'s "single-row already saved by caller" model:
  // here `sourceConfig` is the new persisted state, NOT a payload to
  // re-save on the source. The bulk PUT propagates that state to
  // targets and leaves the source row alone.
  const [pendingPrintAreaApply, setPendingPrintAreaApply] = useState<
    | {
        sourceRow: VariantConfigRow;
        printAreaOverride: PrintAreaVariantConfig['printAreaOverride'];
        printAreaWidthInOverride: number | null;
        printAreaHeightInOverride: number | null;
      }
    | null
  >(null);

  // Variant-scoped print-area editor — only relevant for PER_VARIANT sides
  // and only after the merchant has uploaded an image for the row. Holds
  // the blankVariantId of the row whose override is being edited.
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);

  const uploadBgMutation = useMutation({
    mutationFn: ({
      blankVariantId,
      file,
    }: {
      blankVariantId: string;
      file: File;
    }) =>
      uploadVariantConfigBackground(
        templateId,
        printArea.id,
        blankVariantId,
        file,
      ),
    onSuccess: (data, vars) => {
      applyConfigToCache(vars.blankVariantId, {
        // Same contract as upsertCellMutation.onSuccess: the server
        // always returns a cuid on a successful background upload; the
        // `?? null` keeps the cache shape honest if the field is ever
        // missing rather than silently writing an empty string.
        id: data.id ?? null,
        backgroundColorHex: data.backgroundColorHex ?? null,
        backgroundImageUrl: data.backgroundImageUrl ?? null,
        printAreaOverride: data.printAreaOverride ?? null,
        customizationMandatory: data.customizationMandatory ?? null,
        printAreaWidthInOverride: data.printAreaWidthInOverride ?? null,
        printAreaHeightInOverride: data.printAreaHeightInOverride ?? null,
        scaleCalibrationOverride: data.scaleCalibrationOverride ?? null,
        updatedAt: new Date().toISOString(),
      });
      toast('Background uploaded.', 'success');
      qc.invalidateQueries({ queryKey: ['admin-print-areas', templateId] });

      // Phase 4.0 — give the merchant a one-click path to broadcast the
      // mockup to sibling variants. Skip the dialog when there's nothing
      // to propagate to (single-row matrix).
      if (rows.length > 1 && data.backgroundImageUrl) {
        const sourceRow = rows.find(
          (r) => r.blankVariantId === vars.blankVariantId,
        );
        if (sourceRow) {
          setPendingApply({
            sourceRow,
            backgroundImageUrl: data.backgroundImageUrl,
          });
        }
      }
    },
    onError: (err: Error) => {
      toast(err.message || 'Background upload failed.', 'error');
    },
  });

  const bulkUpsertMutation = useMutation({
    mutationFn: (items: PrintAreaVariantConfig[]) =>
      bulkUpsertVariantConfigs(templateId, printArea.id, items),
    onMutate: async (items) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<VariantConfigRow[]>(queryKey);
      const map = new Map(items.map((i) => [i.blankVariantId, i]));
      replaceCacheRows(map);
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast(err.message || 'Bulk apply failed.', 'error');
    },
    onSuccess: (data) => {
      const map = new Map(data.map((c) => [c.blankVariantId, c]));
      replaceCacheRows(map);
      qc.invalidateQueries({ queryKey: ['admin-print-areas', templateId] });
    },
  });

  // -------------------------------------------------------------------------
  // Auto-detect + apply suggestions
  // -------------------------------------------------------------------------

  const [lastSuggestions, setLastSuggestions] = useState<
    AutoDetectColorSuggestion[] | null
  >(null);

  // Optional palette scope for detection — palette entries win over the
  // static color map when a brand chart (Gildan, Comfort Colors…) is chosen.
  const [detectPaletteId, setDetectPaletteId] = useState<string>('');

  const { data: colorPalettes = [] } = useQuery<
    Array<{ id: string; name: string; isActive: boolean; colorCount: number }>
  >({
    queryKey: ['color-palettes'],
    queryFn: () =>
      api.get<Array<{ id: string; name: string; isActive: boolean; colorCount: number }>>(
        '/api/v1/admin/colors/palettes',
      ),
    staleTime: 60_000,
  });

  const detectColorsMutation = useMutation({
    mutationFn: () =>
      autoDetectApparelColors(templateId, detectPaletteId || undefined),
    onSuccess: (data) => {
      setLastSuggestions(data);
    },
    onError: (err: Error) => {
      toast(err.message || 'Color auto-detect failed.', 'error');
    },
  });

  const applyAutoDetected = useCallback(() => {
    const suggestions = lastSuggestions;
    if (!suggestions || suggestions.length === 0) return;
    if (rows.length === 0) return;

    // Build the canonical full state: every row keeps its current config,
    // EXCEPT rows whose variant has a hex suggestion AND no existing
    // backgroundColorHex (silent — never overwrite a manual override).
    // `buildCanonicalItem` preserves rect + W/H + customization columns
    // verbatim from the cached row so bulk-applying colours never wipes
    // unrelated overrides (regression: see DESIGN_BUG_2026-04-30 in plan
    // a7c91d4e — pre-fix, omitting W/H here silently nulled per-variant
    // sizes for every variant in the print area).
    const items: PrintAreaVariantConfig[] = rows.flatMap((r) => {
      const suggestion = suggestions.find(
        (s) => s.blankVariantId === r.blankVariantId,
      );
      const existingHex = r.currentConfig?.backgroundColorHex ?? null;
      const proposedHex =
        !existingHex && suggestion?.suggestedHex
          ? suggestion.suggestedHex.toUpperCase()
          : existingHex;

      const next = buildCanonicalItem(r, { backgroundColorHex: proposedHex });
      // Skip rows that have no overrides AND no auto-detected hex.
      // `hasAnyConfigField` covers EVERY persisted column (incl. W/H)
      // so a row whose only override is a per-variant size doesn't get
      // silently dropped from the bulk payload (which would cause the
      // canonical-state transaction to delete it).
      return hasAnyConfigField(next) ? [next] : [];
    });

    bulkUpsertMutation.mutate(items, {
      onSuccess: () => toast('Auto-detected colors applied.', 'success'),
    });
  }, [lastSuggestions, rows, bulkUpsertMutation]);

  // -------------------------------------------------------------------------
  // Apply-scope dialog — propagate a freshly uploaded mockup to either
  // every variant sharing the same color option, or every variant in the
  // active print area. Single atomic PUT preserves all sibling overrides
  // because we always send the canonical full state.
  // -------------------------------------------------------------------------

  // Print-area scope handler — broadcasts the freshly persisted rect +
  // W/H to siblings via a single atomic bulk PUT. Mirrors
  // `handlePendingApply` step-for-step (canonical state, "current"
  // short-circuits because the source upsert already persisted).
  const handlePendingPrintAreaApply = useCallback(
    (scope: ApplyScope) => {
      if (!pendingPrintAreaApply) return;
      if (scope.kind === 'current') {
        setPendingPrintAreaApply(null);
        return;
      }

      const {
        sourceRow,
        printAreaOverride,
        printAreaWidthInOverride,
        printAreaHeightInOverride,
      } = pendingPrintAreaApply;
      const sourceId = sourceRow.blankVariantId;

      const isTarget = (row: VariantConfigRow): boolean => {
        if (row.blankVariantId === sourceId) return true;
        if (scope.kind === 'all') return true;
        return rowMatchesOptionScope(row, scope.axisKey, scope.optionValue);
      };

      const items: PrintAreaVariantConfig[] = rows
        .map((r) =>
          buildCanonicalItem(
            r,
            isTarget(r)
              ? {
                  // Propagate the source's full print-area triple
                  // (rect + W/H) atomically — sending only the rect
                  // would leave siblings with the source's new aspect
                  // but their old W/H, producing stretched previews.
                  printAreaOverride,
                  printAreaWidthInOverride,
                  printAreaHeightInOverride,
                }
              : {},
          ),
        )
        .filter(hasAnyConfigField);

      bulkUpsertMutation.mutate(items, {
        onSuccess: () => {
          const targetCount = rows.filter(isTarget).length;
          toast(
            scope.kind === 'all'
              ? `Print area applied to all ${targetCount} variants.`
              : `Print area applied to ${targetCount} variants where ${scope.optionTypeName} = ${scope.optionValue}.`,
            'success',
          );
          setPendingPrintAreaApply(null);
        },
      });
    },
    [pendingPrintAreaApply, rows, bulkUpsertMutation],
  );

  const handlePendingApply = useCallback(
    (scope: ApplyScope) => {
      if (!pendingApply) return;
      if (scope.kind === 'current') {
        // The single-row upload already persisted the change; just close.
        setPendingApply(null);
        return;
      }

      const { sourceRow, backgroundImageUrl } = pendingApply;
      const sourceId = sourceRow.blankVariantId;

      const isTarget = (row: VariantConfigRow): boolean => {
        if (row.blankVariantId === sourceId) return true;
        if (scope.kind === 'all') return true;
        return rowMatchesOptionScope(row, scope.axisKey, scope.optionValue);
      };

      // Target rows take the new image; non-targets keep their existing
      // config verbatim. `buildCanonicalItem` preserves every other
      // persisted column (rect, W/H, required) for both target and
      // non-target rows so bulk-applying a mockup never touches sibling
      // dimension/print-area overrides — see regression note in
      // `applyAutoDetected`.
      const items: PrintAreaVariantConfig[] = rows
        .map((r) =>
          buildCanonicalItem(
            r,
            isTarget(r) ? { backgroundImageUrl } : {},
          ),
        )
        // `hasAnyConfigField` checks W/H too, so rows whose only
        // override is a per-variant size survive the empty-row filter.
        .filter(hasAnyConfigField);

      bulkUpsertMutation.mutate(items, {
        onSuccess: () => {
          const targetCount = rows.filter(isTarget).length;
          toast(
            scope.kind === 'all'
              ? `Mockup applied to all ${targetCount} variants.`
              : `Mockup applied to ${targetCount} variants where ${scope.optionTypeName} = ${scope.optionValue}.`,
            'success',
          );
          setPendingApply(null);
        },
        onError: () => {
          // Error toast is surfaced by the bulk mutation's onError; keep
          // the dialog open so the merchant can retry or cancel.
        },
      });
    },
    [pendingApply, rows, bulkUpsertMutation],
  );

  // -------------------------------------------------------------------------
  // Apply-by-color (replicate one row's color OR image to every variant
  // sharing the same color option value). Atomic bulk PUT.
  // -------------------------------------------------------------------------

  const applyToColor = useCallback(
    (
      sourceRow: VariantConfigRow,
      field: 'backgroundColorHex' | 'backgroundImageUrl',
    ) => {
      const colorValue = sourceRow.colorOptionValue;
      if (!colorValue) {
        toast('This variant has no color option to match.', 'warning');
        return;
      }

      const targets = rows.filter(
        (r) => r.colorOptionValue === colorValue,
      );
      if (targets.length === 0) return;

      const sourceCfg = sourceRow.currentConfig;
      const valueToApply: string | null =
        sourceCfg?.[field] ?? null;

      if (!valueToApply) {
        toast(
          field === 'backgroundColorHex'
            ? 'Source row has no background color set.'
            : 'Source row has no background image set.',
          'warning',
        );
        return;
      }

      // Same canonical-state contract as applyAutoDetected /
      // handlePendingApply: target rows take the source value for the
      // chosen field; everything else (rect, W/H, required) is
      // preserved verbatim from the cached row so this flow never
      // touches sibling overrides.
      const items: PrintAreaVariantConfig[] = rows
        .map((r) => {
          const isTarget = targets.some(
            (t) => t.blankVariantId === r.blankVariantId,
          );
          if (!isTarget) return buildCanonicalItem(r, {});
          return buildCanonicalItem(
            r,
            field === 'backgroundColorHex'
              ? { backgroundColorHex: valueToApply }
              : { backgroundImageUrl: valueToApply },
          );
        })
        .filter(hasAnyConfigField);

      bulkUpsertMutation.mutate(items, {
        onSuccess: () =>
          toast(
            `Applied ${field === 'backgroundColorHex' ? 'color' : 'mockup'} to ${targets.length} variant${targets.length === 1 ? '' : 's'} with color "${colorValue}".`,
            'success',
          ),
      });
    },
    [rows, bulkUpsertMutation],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-6 py-10">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <p className="text-sm text-text-secondary">
          {error instanceof Error ? error.message : 'Failed to load variants.'}
        </p>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-6 py-10 text-center text-sm text-text-secondary">
        Add blank variants to this template (Variants tab) before configuring
        per-variant overrides.
      </div>
    );
  }

  // Single source of truth for "block all editable controls while a
  // server round-trip is in flight". MUST list every mutation that
  // mutates the active print area's variant configs — otherwise the
  // affected button stays clickable and the user can fire concurrent
  // requests (e.g. double-click Reset → two delete calls; change a
  // hex while a background upload is mid-flight → server merges the
  // two payloads in undefined order).
  const isAnyMutating =
    upsertCellMutation.isPending ||
    bulkUpsertMutation.isPending ||
    detectColorsMutation.isPending ||
    resetRowMutation.isPending ||
    uploadBgMutation.isPending;

  const isMaskMode = printArea.mockupMode === 'MASK';

  return (
    <div className="space-y-4 px-6 py-5">
      {/* Auto-detect colours is meaningful ONLY in MASK mode — in
          PER_VARIANT every variant ships its own fully-coloured photo
          and the per-variant `backgroundColor` field is unread by the
          designer (see variant-resolver.ts). */}
      {isMaskMode && (
        <AutoDetectColorsBar
          rows={rows}
          suggestions={lastSuggestions}
          isDetecting={detectColorsMutation.isPending}
          isApplying={bulkUpsertMutation.isPending}
          // Pass the aggregate so the Detect/Apply/Dismiss buttons stay
          // disabled while ANY single-cell upsert / reset / background
          // upload is in flight — otherwise auto-detect's atomic bulk
          // PUT can trample an in-flight per-row mutation.
          isBlocked={isAnyMutating}
          palettes={colorPalettes}
          paletteId={detectPaletteId}
          onPaletteChange={setDetectPaletteId}
          onDetect={() => detectColorsMutation.mutate()}
          onApply={applyAutoDetected}
          onDismiss={() => setLastSuggestions(null)}
        />
      )}

      {pendingApply && (
        <ApplyToScopeDialog
          open
          changeType="backgroundImage"
          sourceRow={pendingApply.sourceRow}
          allRows={rows}
          printAreaLabel={printArea.displayName}
          isApplying={bulkUpsertMutation.isPending}
          onApply={handlePendingApply}
          onCancel={() => setPendingApply(null)}
        />
      )}

      {pendingPrintAreaApply && (
        <ApplyToScopeDialog
          open
          changeType="printAreaOverride"
          sourceRow={pendingPrintAreaApply.sourceRow}
          allRows={rows}
          printAreaLabel={printArea.displayName}
          isApplying={bulkUpsertMutation.isPending}
          onApply={handlePendingPrintAreaApply}
          onCancel={() => setPendingPrintAreaApply(null)}
        />
      )}

      {editingVariantId && (
        <Suspense fallback={null}>
          {(() => {
            const editingRow = rows.find(
              (r) => r.blankVariantId === editingVariantId,
            );
            if (!editingRow) return null;
            return (
              <VariantPrintAreaEditorModal
                open
                printArea={printArea}
                mockupMode={printArea.mockupMode}
                row={editingRow}
                saving={upsertCellMutation.isPending}
                onClose={() => {
                  if (upsertCellMutation.isPending) return;
                  setEditingVariantId(null);
                }}
                onSave={({ rect, widthIn, heightIn, scaleCalibration }) => {
                  // Persist rect + W/H atomically. Wire convention:
                  //   • W or H equal to side default → send `null`
                  //     (clear the override column so the row inherits
                  //     cleanly the next time the side W/H changes).
                  //   • Otherwise send the typed number verbatim.
                  // Sending the W/H separately from the rect would
                  // briefly leave the renderer with a stretched rect
                  // (rect snapped to new ratio, but W/H still old).
                  const widthInPayload =
                    widthIn === printArea.width ? null : widthIn;
                  const heightInPayload =
                    heightIn === printArea.height ? null : heightIn;
                  upsertCellMutation.mutate(
                    {
                      blankVariantId: editingRow.blankVariantId,
                      payload: {
                        printAreaOverride: rect,
                        printAreaWidthInOverride: widthInPayload,
                        printAreaHeightInOverride: heightInPayload,
                        // Calibration breadcrumb is authoring-only; the
                        // designer renders from rect + W/H. Persist it so
                        // re-opening the editor restores Scale-to-product.
                        scaleCalibrationOverride: scaleCalibration,
                      },
                    },
                    {
                      onSuccess: () => {
                        toast('Print area saved.', 'success');
                        setEditingVariantId(null);
                        // Offer bulk-apply to siblings — same UX
                        // contract as background-image upload. Skip
                        // the dialog when there's nothing to
                        // propagate to (single-row matrix); the
                        // single-row mutate above already persisted
                        // the change.
                        if (rows.length > 1) {
                          setPendingPrintAreaApply({
                            sourceRow: editingRow,
                            printAreaOverride: rect,
                            printAreaWidthInOverride: widthInPayload,
                            printAreaHeightInOverride: heightInPayload,
                          });
                        }
                      },
                    },
                  );
                }}
                onResetToInherit={() => {
                  // Wipe ALL override columns so the row falls back to
                  // side defaults fully (rect + W + H + calibration). The
                  // server keeps the row alive (variantConfigCount
                  // unaffected) — the renderer just resolves through
                  // the side values via the §3.2 priority chain.
                  upsertCellMutation.mutate(
                    {
                      blankVariantId: editingRow.blankVariantId,
                      payload: {
                        printAreaOverride: null,
                        printAreaWidthInOverride: null,
                        printAreaHeightInOverride: null,
                        scaleCalibrationOverride: null,
                      },
                    },
                    {
                      onSuccess: () => {
                        toast('Print area reset to side default.', 'success');
                        setEditingVariantId(null);
                      },
                    },
                  );
                }}
              />
            );
          })()}
        </Suspense>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-secondary">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                Variant
              </th>
              {isMaskMode ? (
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Background color
                </th>
              ) : (
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Mockup image
                </th>
              )}
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                <span className="inline-flex items-center gap-1">
                  Required
                  <RequiredHelpTooltip />
                </span>
              </th>
              {/* Dedicated per-variant print-area column (both modes).
                  MASK rows previously had NO entry point to the
                  print-area editor at all — the button only lived inside
                  the PER_VARIANT mockup cell. */}
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                Print area
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {rows.map((row) => (
              <VariantConfigRowEditor
                key={row.blankVariantId}
                row={row}
                mockupMode={printArea.mockupMode}
                sideHasMockup={!!printArea.mockupImageUrl}
                disabled={isAnyMutating}
                isUploadingBg={
                  uploadBgMutation.isPending &&
                  uploadBgMutation.variables?.blankVariantId ===
                    row.blankVariantId
                }
                onChangeColor={(hex) =>
                  upsertCellMutation.mutate({
                    blankVariantId: row.blankVariantId,
                    payload: { backgroundColorHex: hex },
                  })
                }
                onChangeRequired={(value) =>
                  upsertCellMutation.mutate({
                    blankVariantId: row.blankVariantId,
                    payload: { customizationMandatory: value },
                  })
                }
                onUploadBg={(file) =>
                  uploadBgMutation.mutate({
                    blankVariantId: row.blankVariantId,
                    file,
                  })
                }
                onClearBg={() =>
                  upsertCellMutation.mutate({
                    blankVariantId: row.blankVariantId,
                    payload: { backgroundImageUrl: '' },
                  })
                }
                onApplyColorToMatching={() =>
                  applyToColor(row, 'backgroundColorHex')
                }
                onApplyImageToMatching={() =>
                  applyToColor(row, 'backgroundImageUrl')
                }
                onEditPrintArea={() => setEditingVariantId(row.blankVariantId)}
                onReset={() => resetRowMutation.mutate(row.blankVariantId)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Required header tooltip — explains what Inherit / Required / Optional do
// for the per-variant `customizationMandatory` override.
// ---------------------------------------------------------------------------

function RequiredHelpTooltip() {
  return (
    <span
      className="group relative inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-surface-tertiary text-[10px] font-bold normal-case text-text-tertiary"
      tabIndex={0}
      role="img"
      aria-label="What does Required mean?"
    >
      ?
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-72 -translate-x-1/2 whitespace-normal rounded-md border border-border bg-surface px-3 py-2 text-left text-xs font-normal normal-case text-text-primary shadow-lg group-hover:block group-focus:block"
      >
        <strong className="block pb-1 text-text-primary">
          Per-variant customization rule
        </strong>
        Overrides the side's default <em>Customization required</em> toggle
        for THIS variant only:
        <ul className="mt-1 list-disc pl-4 text-text-secondary">
          <li>
            <strong className="text-text-primary">Inherit</strong> — use the
            side default (most rows pick this).
          </li>
          <li>
            <strong className="text-text-primary">Required</strong> — designer
            blocks <em>Create Product</em> if no design on this side.
          </li>
          <li>
            <strong className="text-text-primary">Optional</strong> — designer
            allows save with no design on this side.
          </li>
        </ul>
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Auto-detect colors banner
// ---------------------------------------------------------------------------

function AutoDetectColorsBar({
  rows,
  suggestions,
  isDetecting,
  isApplying,
  isBlocked,
  palettes,
  paletteId,
  onPaletteChange,
  onDetect,
  onApply,
  onDismiss,
}: {
  rows: VariantConfigRow[];
  suggestions: AutoDetectColorSuggestion[] | null;
  isDetecting: boolean;
  isApplying: boolean;
  /**
   * True when any sibling mutation (single-cell upsert, reset, bg upload)
   * is pending. Auto-detect issues a bulk PUT that owns the canonical
   * full state of the print area, so we MUST avoid kicking it off while
   * another mutation is still being merged on the server.
   */
  isBlocked: boolean;
  palettes: Array<{ id: string; name: string; isActive: boolean; colorCount: number }>;
  paletteId: string;
  onPaletteChange: (id: string) => void;
  onDetect: () => void;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const stats = useMemo(() => {
    if (!suggestions) return null;
    const resolved = suggestions.filter((s) => s.suggestedHex).length;
    const unresolved = suggestions
      .filter((s) => !s.suggestedHex)
      .map((s) => {
        const row = rows.find((r) => r.blankVariantId === s.blankVariantId);
        return row?.colorOptionValue ?? row?.optionsLabel ?? s.blankVariantId;
      });
    const willApply = suggestions.filter((s) => {
      const row = rows.find((r) => r.blankVariantId === s.blankVariantId);
      return s.suggestedHex && !row?.currentConfig?.backgroundColorHex;
    }).length;
    return {
      total: suggestions.length,
      resolved,
      willApply,
      unresolved: Array.from(new Set(unresolved)),
    };
  }, [suggestions, rows]);

  return (
    <div className="flex flex-wrap items-start gap-3 rounded-lg border border-border bg-surface-secondary px-4 py-3">
      <div className="flex-1 min-w-[16rem]">
        <p className="text-sm font-medium text-text-primary">
          Auto-detect colors from variant names
        </p>
        <p className="mt-0.5 text-xs text-text-secondary">
          Resolves color names ("Black", "Navy", "Forest Green") to a hex via
          the color library (palette entries first, built-in color map as
          fallback). Manual overrides are never replaced.
        </p>
        {palettes.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <label className="text-xs font-medium text-text-secondary">Palette:</label>
            <select
              value={paletteId}
              onChange={(e) => onPaletteChange(e.target.value)}
              disabled={isDetecting || isApplying || isBlocked}
              className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">All palettes + color map</option>
              {palettes
                .filter((p) => p.isActive)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.colorCount})
                  </option>
                ))}
            </select>
          </div>
        )}
        {stats && (
          <div className="mt-2 space-y-1 text-xs">
            <p className="text-text-primary">
              <span className="font-semibold">
                {stats.resolved} / {stats.total}
              </span>{' '}
              variants matched.{' '}
              {stats.resolved === 0
                ? 'No variants matched the color map. Add a hex code to the underlying option values, or set per-variant overrides below.'
                : stats.willApply > 0
                  ? `${stats.willApply} will be applied (skipping rows with manual overrides).`
                  : 'All matched variants already have a manual override — nothing to apply.'}
            </p>
            {stats.unresolved.length > 0 && (
              <p className="text-warning-700">
                Unresolved: {stats.unresolved.join(', ')}. Set them manually
                in the table below.
              </p>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onDetect}
          disabled={isDetecting || isApplying || isBlocked}
        >
          {isDetecting ? 'Detecting…' : 'Detect colors'}
        </Button>
        {stats && stats.willApply > 0 && (
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={onApply}
            disabled={isApplying || isBlocked}
          >
            {isApplying ? 'Applying…' : `Apply ${stats.willApply}`}
          </Button>
        )}
        {suggestions && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            disabled={isApplying || isBlocked}
          >
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single row editor
// ---------------------------------------------------------------------------

interface RowEditorProps {
  row: VariantConfigRow;
  /**
   * Active print area's mockup mode. Drives which middle-column cell
   * we render: MASK → background colour picker; PER_VARIANT → mockup
   * image upload + bulk-apply + (Phase 6.5+) per-variant print-area
   * editor entry-point.
   */
  mockupMode: PrintAreaMockupMode;
  /**
   * Whether the SIDE itself has a mockup uploaded
   * (`printArea.mockupImageUrl`). Gates the print-area editor button:
   * MASK mode needs the side mask PNG as the canvas background, and
   * PER_VARIANT rows without their own image fall back to it too.
   */
  sideHasMockup: boolean;
  disabled: boolean;
  isUploadingBg: boolean;
  onChangeColor: (hex: string | null) => void;
  onChangeRequired: (value: boolean | null) => void;
  onUploadBg: (file: File) => void;
  onClearBg: () => void;
  onApplyColorToMatching: () => void;
  onApplyImageToMatching: () => void;
  /** Open the per-variant print-area editor (both mockup modes). */
  onEditPrintArea: () => void;
  onReset: () => void;
}

function VariantConfigRowEditor({
  row,
  mockupMode,
  sideHasMockup,
  disabled,
  isUploadingBg,
  onChangeColor,
  onChangeRequired,
  onUploadBg,
  onClearBg,
  onApplyColorToMatching,
  onApplyImageToMatching,
  onEditPrintArea,
  onReset,
}: RowEditorProps) {
  const cfg = row.currentConfig;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Hex input is decoupled from the persisted value so the merchant can
  // type freely without firing a request on every keystroke.
  const [draftHex, setDraftHex] = useState<string>(
    cfg?.backgroundColorHex ?? '',
  );
  const hexInputRef = useRef<HTMLInputElement | null>(null);
  // Keep the local draft in sync when the persisted value changes from
  // outside (e.g. bulk-apply, auto-detect). We never overwrite mid-edit:
  // if the hex input is focused the merchant is actively typing.
  useEffect(() => {
    if (document.activeElement === hexInputRef.current) return;
    setDraftHex(cfg?.backgroundColorHex ?? '');
  }, [cfg?.backgroundColorHex]);

  const commitHex = () => {
    if (draftHex === '' || draftHex === null) {
      if (cfg?.backgroundColorHex) {
        onChangeColor(null);
      }
      return;
    }
    const normalized = normalizeHex(draftHex);
    if (!normalized) {
      toast('Enter a 6-digit hex color (e.g. #1A2B3C).', 'error');
      setDraftHex(cfg?.backgroundColorHex ?? '');
      return;
    }
    if (normalized === cfg?.backgroundColorHex) return;
    setDraftHex(normalized);
    onChangeColor(normalized);
  };

  const requiredValue: 'true' | 'false' | 'inherit' =
    cfg?.customizationMandatory === true
      ? 'true'
      : cfg?.customizationMandatory === false
        ? 'false'
        : 'inherit';

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    onUploadBg(file);
  };

  // Drives the Reset action's enable-state. MUST cover every persisted
  // override column on `PrintAreaVariantConfig` — otherwise a row whose
  // only override is a per-variant W or H ends up with Reset stuck
  // disabled, leaving the merchant with no way to clear it from the
  // matrix (would have to re-open the editor and click "Reset to
  // side default" inside the modal).
  const hasAnyOverride =
    cfg !== null &&
    (cfg.backgroundColorHex !== null ||
      cfg.backgroundImageUrl !== null ||
      cfg.printAreaOverride !== null ||
      cfg.customizationMandatory !== null ||
      cfg.printAreaWidthInOverride !== null ||
      cfg.printAreaHeightInOverride !== null);

  const isMaskMode = mockupMode === 'MASK';

  // Print-area editor gating — the modal needs SOMETHING to draw under
  // the rectangle, otherwise the merchant positions a rect on a blank
  // square. MASK mode strictly needs the side mask PNG (the modal
  // ignores per-variant images there, mirroring variant-resolver.ts);
  // PER_VARIANT accepts the variant's own mockup or the side fallback.
  const canEditPrintArea = isMaskMode
    ? sideHasMockup
    : Boolean(cfg?.backgroundImageUrl) || sideHasMockup;

  // Asterisk surfaces ANY persisted per-variant print-area override —
  // rect, width-only, or height-only. Mirrors the modal's `hasOverride`
  // check (VariantPrintAreaEditorModal) so the two indicators never
  // disagree.
  const hasPrintAreaOverride =
    cfg !== null &&
    (cfg.printAreaOverride !== null ||
      cfg.printAreaWidthInOverride !== null ||
      cfg.printAreaHeightInOverride !== null);

  return (
    <tr>
      {/* Variant label */}
      <td className="whitespace-nowrap px-4 py-3 align-top">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-text-primary">
            {row.optionsLabel}
          </span>
          <span className="text-xs text-text-tertiary">{row.sku}</span>
        </div>
      </td>

      {isMaskMode ? (
        // -------------------------------------------------------------------
        // MASK mode — Background color cell.
        // The colour fills the entire 1024×1024 viewport behind the mockup
        // PNG (which acts as a colourless mask). PER_VARIANT mode never
        // reads `backgroundColorHex` (see variant-resolver.ts), so we hide
        // the picker entirely to avoid storing values the renderer ignores.
        // -------------------------------------------------------------------
        <td className="px-4 py-3 align-top">
          <div className="flex flex-wrap items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-7 w-7 flex-shrink-0 rounded border border-border"
              style={{
                backgroundColor: cfg?.backgroundColorHex ?? 'transparent',
                backgroundImage: cfg?.backgroundColorHex
                  ? undefined
                  : 'repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%) 50% / 8px 8px',
              }}
            />
            <input
              type="color"
              aria-label="Background color picker"
              disabled={disabled}
              className="h-7 w-7 cursor-pointer rounded border border-border bg-surface p-0"
              value={cfg?.backgroundColorHex ?? '#ffffff'}
              onChange={(e) => onChangeColor(e.target.value.toUpperCase())}
            />
            <Input
              ref={hexInputRef}
              value={draftHex}
              disabled={disabled}
              placeholder="#000000"
              aria-label="Background hex"
              onChange={(e) => setDraftHex(e.target.value)}
              onBlur={commitHex}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitHex();
                }
              }}
              className="!h-8 w-28 font-mono text-xs"
            />
            {cfg?.backgroundColorHex && row.colorOptionValue && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={onApplyColorToMatching}
                title={`Apply this color to every variant where Color = ${row.colorOptionValue}`}
              >
                Apply to "{row.colorOptionValue}"
              </Button>
            )}
          </div>
        </td>
      ) : (
        // -------------------------------------------------------------------
        // PER_VARIANT mode — Mockup image cell.
        // Uploads land via `uploadVariantConfigBackground` which triggers
        // ApplyToScopeDialog so a single upload can broadcast to every
        // sibling variant sharing the same colour ("All Black variants").
        // The per-variant print-area editor lives in its own dedicated
        // "Print area" column (shared with MASK mode) — see below.
        // accept="" mirrors the server-side TRANSPARENCY_PRESERVING set
        // for PER_VARIANT (PNG/WebP/AVIF/JPEG) — see SidesService.uploadVariantConfigBackground.
        // -------------------------------------------------------------------
        <td className="px-4 py-3 align-top">
          <div className="flex items-start gap-2">
            <div
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-surface-tertiary"
              aria-label="Variant mockup preview"
            >
              {cfg?.backgroundImageUrl ? (
                // `crossOrigin="anonymous"` keeps the browser's cache entry
                // CORS-validated so the designer's Konva canvas (which loads
                // the SAME URL with `crossOrigin="anonymous"` because it
                // needs `toDataURL`/export) can reuse the cached resource.
                // Without this, admin renders the thumbnail without an
                // Origin check, the browser caches a "non-CORS" entry, and
                // designer's CORS-aware fetch sometimes treats the cached
                // entry as tainted → flaky mockup rendering across variants.
                <img
                  src={cfg.backgroundImageUrl}
                  crossOrigin="anonymous"
                  alt={`Mockup for ${row.optionsLabel}`}
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="px-1 text-center text-[9px] text-text-tertiary">
                  Inherit
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled || isUploadingBg}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploadingBg
                  ? 'Uploading…'
                  : cfg?.backgroundImageUrl
                    ? 'Replace'
                    : 'Upload'}
              </Button>
              {cfg?.backgroundImageUrl && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={disabled || isUploadingBg}
                    onClick={onClearBg}
                  >
                    Clear
                  </Button>
                  {row.colorOptionValue && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={disabled || isUploadingBg}
                      onClick={onApplyImageToMatching}
                      title={`Apply this mockup to every variant where Color = ${row.colorOptionValue}`}
                    >
                      Apply to "{row.colorOptionValue}"
                    </Button>
                  )}
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/avif"
                className="hidden"
                onChange={handleFilePick}
              />
            </div>
          </div>
        </td>
      )}

      {/* Customization required override */}
      <td className="px-4 py-3 align-top">
        <select
          disabled={disabled}
          value={requiredValue}
          onChange={(e) => {
            const next = e.target.value as 'true' | 'false' | 'inherit';
            onChangeRequired(
              next === 'inherit' ? null : next === 'true' ? true : false,
            );
          }}
          className="h-8 rounded-md border border-border bg-surface px-2 text-sm text-text-primary"
        >
          <option value="inherit">Inherit</option>
          <option value="true">Required</option>
          <option value="false">Optional</option>
        </select>
      </td>

      {/* Per-variant print area — dedicated column for BOTH modes.
          The button is always rendered (discoverability) but disabled
          until the editor has a usable canvas background:
            MASK        → the side mask PNG must be uploaded; the modal
                          composites it over the variant's detected /
                          assigned background colour.
            PER_VARIANT → the variant's own mockup, falling back to the
                          side mockup when the row has no image yet.
          Override is stored on PrintAreaVariantConfig.printAreaOverrideJson
          and read by the designer's variant-resolver; saving re-opens the
          ApplyToScopeDialog so the rect can be bulk-applied to siblings. */}
      <td className="px-4 py-3 align-top">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || isUploadingBg || !canEditPrintArea}
          onClick={onEditPrintArea}
          title={
            canEditPrintArea
              ? isMaskMode
                ? "Position the print area on the side mask over this variant's background color. Aspect ratio recomputes from the per-variant Width × Height."
                : "Position the print area on this variant's mockup. Aspect ratio recomputes from the per-variant Width × Height."
              : isMaskMode
                ? 'Upload the side mask PNG first — the editor needs it as the canvas background.'
                : 'Upload a mockup for this variant (or a side mockup) first — the editor needs it as the canvas background.'
          }
        >
          {hasPrintAreaOverride ? 'Edit print area*' : 'Edit print area'}
        </Button>
      </td>

      {/* Reset action */}
      <td className="px-4 py-3 text-right align-top">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled || !hasAnyOverride}
          onClick={() => {
            if (
              window.confirm(
                'Clear all overrides for this variant? The designer will fall back to the side defaults.',
              )
            ) {
              onReset();
            }
          }}
        >
          Reset
        </Button>
      </td>
    </tr>
  );
}
